import { useState, useEffect, useMemo, useContext } from 'react';
import { CalendarIcon } from 'lucide-react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Field, FieldGroup, FieldLabel, FieldSet } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  BillingFrequency,
  CustomFieldValueInput,
} from '@/graphql/generatedTypes';
import { StoreAssetV2FragmentFragment } from '@/graphql/fragments/storeAssetV2.generated';
import { CurrencySmartInput } from './CurrencySmartInput';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from 'sonner';
import { ItemType } from '../useAllAssetTypes';
import { AssetCustomFields } from './AssetCustomFields';
import { validateRequiredCustomFields } from './customFieldUtils';
import { billingEndToLastBilledDay, getBackendErrorDetail } from '../utils';
import {
  CanAccessAssetInternalComment,
  ViewerContext,
} from '@/providers/ViewerContextProvider';
import i18n from '@/utils/i18n';

export type AssetFormState = {
  itemTypeId: number | null;
  description: string;
  internalComment: string;
  price: number;
  billingFrequency: BillingFrequency;
  billingStartOption: 'onboarding' | 'custom';
  billingStart: Date | null;
  billingEndOption: 'none' | 'custom';
  billingEnd: Date | null;
  isBillable: boolean;
  customFields: CustomFieldValueInput[];
};

const initialFormState: AssetFormState = {
  itemTypeId: null,
  description: '',
  internalComment: '',
  price: 0,
  billingFrequency: BillingFrequency.Monthly,
  billingStartOption: 'onboarding',
  billingStart: null,
  billingEndOption: 'none',
  billingEnd: null,
  isBillable: true,
  customFields: [],
};

function getFormStateFromAsset(
  asset: StoreAssetV2FragmentFragment,
): AssetFormState {
  return {
    itemTypeId: asset.item_type.id,
    description: asset.description ?? '',
    internalComment: asset.internal_comment ?? '',
    price: asset.price ?? 0,
    billingFrequency: asset.billing_frequency ?? BillingFrequency.Monthly,
    billingStartOption: asset.billing_start_with_onboarding
      ? 'onboarding'
      : 'custom',
    billingStart: asset.billing_start ? new Date(asset.billing_start) : null,
    billingEndOption: asset.billing_end ? 'custom' : 'none',
    // Stored billing_end is exclusive (first un-billed day); the picker shows the
    // inclusive last billed day, so step back a day. See utils conversion helpers.
    billingEnd: asset.billing_end
      ? billingEndToLastBilledDay(asset.billing_end)
      : null,
    isBillable: asset.is_billable ?? true,
    customFields:
      asset.custom_fields?.map((f) => ({ id: f.id, value: f.value })) ?? [],
  };
}

type AssetFormDialogMode = 'add' | 'edit' | 'view';

interface AssetFormDialogProps {
  mode: AssetFormDialogMode;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currencySymbol: string;
  onboardedDate: Date | null;
  loading?: boolean;
  // For add mode
  itemTypes?: ItemType[];
  onAdd?: (data: AssetFormState) => void | Promise<void>;
  // For edit/view mode
  asset?: StoreAssetV2FragmentFragment | null;
  onEdit?: (assetId: string, data: AssetFormState) => Promise<void>;
  children?: React.ReactNode;
}

export function AssetFormDialog({
  mode,
  isOpen,
  onOpenChange,
  currencySymbol,
  onboardedDate,
  loading = false,
  itemTypes = [],
  onAdd,
  asset,
  onEdit,
  children,
}: AssetFormDialogProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [formState, setFormState] = useState<AssetFormState>(initialFormState);

  // Reset form when dialog opens/closes or asset changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'add') {
        setFormState(initialFormState);
      } else if (asset) {
        setFormState(getFormStateFromAsset(asset));
      }
    }
  }, [isOpen, mode, asset]);

  const isReadOnly = mode === 'view';
  const isAddMode = mode === 'add';

  // colugo nulls the field out for everyone else (@assetInternalCommentAuth)
  // and drops their writes, so without this gate those roles get an empty box
  // that swallows whatever they type.
  const viewer = useContext(ViewerContext);
  const canAccessInternalComment = CanAccessAssetInternalComment(viewer);

  // Billing may not start in a calendar month that has already been invoiced.
  // The backend rejects a billing_start before the asset's arrival month
  // (assertBillingStartNotBeforeArrivalMonth), so restrict the picker to the
  // first day of the current month onward. An existing asset may already bill
  // from an earlier month — an asset created with "start with onboarding" keeps
  // the store's onboarding date — and the backend keeps that month allowed, so
  // lower the floor to it instead of showing the stored day as out of range.
  const minBillingDate = useMemo(() => {
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const storedStart = asset?.billing_start
      ? new Date(asset.billing_start)
      : null;
    if (storedStart == null) return currentMonthStart;
    const storedMonthStart = new Date(
      storedStart.getFullYear(),
      storedStart.getMonth(),
      1,
    );
    return storedMonthStart < currentMonthStart
      ? storedMonthStart
      : currentMonthStart;
  }, [asset?.billing_start]);

  // The picker holds the inclusive last billed day. The backend needs the
  // exclusive end (= last day + 1) to be strictly after billing_start
  // (assertBillingEndAfterStart), which means the inclusive last day may be the
  // start day itself (a single-day billing window) but no earlier. So restrict
  // the picker to the effective start — the custom start date, or the onboarding
  // date when the asset bills from onboarding.
  const minBillingEndDate = useMemo(() => {
    const start =
      formState.billingStartOption === 'custom' && formState.billingStart
        ? formState.billingStart
        : (onboardedDate ?? new Date());
    return new Date(start.getFullYear(), start.getMonth(), start.getDate());
  }, [formState.billingStartOption, formState.billingStart, onboardedDate]);

  // Get custom field definitions for the selected item type
  const customFieldDefinitions = useMemo(() => {
    if (isAddMode) {
      const selectedType = itemTypes.find((t) => t.id === formState.itemTypeId);
      return selectedType?.custom_field_definitions ?? [];
    }
    return asset?.item_type?.custom_field_definitions ?? [];
  }, [isAddMode, itemTypes, formState.itemTypeId, asset]);

  const hasRequiredCustomFields = useMemo(
    () =>
      validateRequiredCustomFields(
        customFieldDefinitions,
        formState.customFields,
      ),
    [customFieldDefinitions, formState.customFields],
  );

  const canSave = isAddMode
    ? formState.itemTypeId !== null && hasRequiredCustomFields
    : hasRequiredCustomFields;

  // Reset custom fields when item type changes in add mode
  useEffect(() => {
    if (isAddMode && formState.itemTypeId) {
      setFormState((prev) => ({ ...prev, customFields: [] }));
    }
  }, [formState.itemTypeId, isAddMode]);

  const handleSave = async () => {
    if (isAddMode) {
      setIsSaving(true);
      try {
        await onAdd?.(formState);
        toast.success(i18n.t('Assets:assetAddedSuccessfully'));
        setFormState(initialFormState);
        onOpenChange(false);
      } catch (err) {
        const detail = getBackendErrorDetail(err);
        toast.error(
          i18n.t('Assets:errorAddingAsset'),
          detail ? { description: detail } : undefined,
        );
      }
      setIsSaving(false);
    } else if (asset && onEdit) {
      setIsSaving(true);
      try {
        await onEdit(asset.id, formState);
        toast.success(i18n.t('Assets:assetSavedSuccessfully'));
      } catch (err) {
        const detail = getBackendErrorDetail(err);
        toast.error(
          i18n.t('Assets:errorSavingAsset'),
          detail ? { description: detail } : undefined,
        );
      }
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setFormState(initialFormState);
    onOpenChange(false);
  };

  // For view/edit mode, don't render if no asset
  if (!isAddMode && !asset) {
    return null;
  }

  const dialogTitle = isAddMode
    ? i18n.t('Assets:addAsset')
    : (asset?.item_type.name ?? 'Asset');

  const dialogDescription = isAddMode
    ? i18n.t('Assets:addNewAssetToStore')
    : String(asset?.item_type.id ?? '');

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="tw:sm:max-w-[440px] tw:px-5">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription className="tw:hidden">
            {dialogDescription}
          </DialogDescription>
        </DialogHeader>
        <DialogBody gradientHeight={56}>
          {loading ? (
            <div className="tw:flex tw:items-center tw:justify-center tw:py-12">
              <Spinner className="tw:size-8" />
            </div>
          ) : (
            <>
              <FieldGroup className="tw:gap-4">
                {/* Row 1: Asset Type (add mode) or ID + Type (edit/view mode) */}
                {isAddMode ? (
                  <FieldSet>
                    <Field>
                      <FieldLabel>{i18n.t('Assets:assetType')}</FieldLabel>
                      <Select
                        value={formState.itemTypeId?.toString() ?? ''}
                        onValueChange={(value) =>
                          setFormState((prev) => ({
                            ...prev,
                            itemTypeId: parseInt(value, 10),
                          }))
                        }
                      >
                        <SelectTrigger className="tw:w-full">
                          <SelectValue placeholder={i18n.t('Common:select')} />
                        </SelectTrigger>
                        <SelectContent>
                          {itemTypes.map((itemType) => (
                            <SelectItem
                              key={itemType.id}
                              value={itemType.id.toString()}
                            >
                              {itemType.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldSet>
                ) : (
                  <FieldSet className="tw:grid tw:grid-cols-2">
                    <Field>
                      <FieldLabel>ID</FieldLabel>
                      <Input value={`#${asset?.id}`} readOnly disabled />
                    </Field>
                    <Field>
                      <FieldLabel>{i18n.t('Common:type')}</FieldLabel>
                      <Input
                        value={asset?.item_type.name ?? ''}
                        readOnly
                        disabled
                      />
                    </Field>
                  </FieldSet>
                )}

                {/* Row 2: Description */}
                <FieldSet>
                  <Field>
                    <FieldLabel htmlFor="asset-description">
                      {i18n.t('Assets:descriptionWillBeSeenOnInvoice')}
                    </FieldLabel>
                    <Textarea
                      id="asset-description"
                      value={formState.description}
                      onChange={(e) =>
                        setFormState((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder={i18n.t('Assets:enterDescription')}
                      disabled={isReadOnly}
                      className="tw:resize-none tw:max-h-[120px]"
                    />
                  </Field>
                </FieldSet>

                {/* Row 3: Internal comment — never leaves the admin UI, in
                    contrast to the description above, which the partner reads
                    on the invoice. */}
                {canAccessInternalComment && (
                  <FieldSet>
                    <Field>
                      <FieldLabel htmlFor="asset-internal-comment">
                        {i18n.t('Assets:internalComment')}
                      </FieldLabel>
                      <Textarea
                        id="asset-internal-comment"
                        value={formState.internalComment}
                        onChange={(e) =>
                          setFormState((prev) => ({
                            ...prev,
                            internalComment: e.target.value,
                          }))
                        }
                        placeholder={i18n.t('Assets:enterInternalComment')}
                        disabled={isReadOnly}
                        className="tw:resize-none tw:max-h-[120px]"
                      />
                      <p className="tw:text-xs tw:text-muted-foreground">
                        {i18n.t('Assets:internalCommentHint')}
                      </p>
                    </Field>
                  </FieldSet>
                )}

                {/* Row 4: Price and Billing cycle */}
                <FieldSet className="tw:grid tw:grid-cols-2">
                  <Field>
                    <FieldLabel>{i18n.t('ItemType:price')}</FieldLabel>
                    <CurrencySmartInput
                      value={formState.price}
                      onValueChange={(value) =>
                        setFormState((prev) => ({ ...prev, price: value }))
                      }
                      locale={new Intl.NumberFormat().resolvedOptions().locale}
                      currencySymbol={currencySymbol}
                      disabled={isReadOnly}
                    />
                  </Field>
                  <Field>
                    <FieldLabel>{i18n.t('Assets:billingCycle')}</FieldLabel>
                    <Select
                      value={formState.billingFrequency}
                      onValueChange={(value) =>
                        setFormState((prev) => ({
                          ...prev,
                          billingFrequency: value as BillingFrequency,
                        }))
                      }
                      disabled={isReadOnly}
                    >
                      <SelectTrigger className="tw:w-full">
                        <SelectValue placeholder={i18n.t('Common:select')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={BillingFrequency.Monthly}>
                          {i18n.t('ItemType:billingFrequencyMonthly')}
                        </SelectItem>
                        <SelectItem value={BillingFrequency.Yearly}>
                          {i18n.t('ItemType:billingFrequencyYearly')}
                        </SelectItem>
                        <SelectItem value={BillingFrequency.OneTime}>
                          {i18n.t('ItemType:billingFrequencyOneTime')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </FieldSet>

                {/* Row 5: First day of billing */}
                <FieldSet className="tw:grid tw:grid-cols-2">
                  <Field>
                    <FieldLabel>
                      {i18n.t('Assets:firstDayOfBilling')}
                    </FieldLabel>
                    <Select
                      value={formState.billingStartOption}
                      onValueChange={(value) =>
                        setFormState((prev) => ({
                          ...prev,
                          billingStartOption: value as 'onboarding' | 'custom',
                        }))
                      }
                      disabled={isReadOnly}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={i18n.t('Common:select')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="onboarding">
                          {i18n.t('Assets:withOnboarding')}
                        </SelectItem>
                        <SelectItem value="custom">
                          {i18n.t('Assets:custom')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field>
                    <FieldLabel className="tw:invisible">
                      {i18n.t('Common:dateTime')}
                    </FieldLabel>
                    {formState.billingStartOption === 'onboarding' ? (
                      <Button
                        variant="outline"
                        className="tw:w-full tw:justify-start tw:text-left tw:font-normal"
                        disabled
                      >
                        <CalendarIcon />
                        {onboardedDate
                          ? Intl.DateTimeFormat(
                              new Intl.NumberFormat().resolvedOptions().locale,
                            ).format(onboardedDate)
                          : i18n.t('Common:notSet')}
                      </Button>
                    ) : (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              'tw:w-full tw:justify-start tw:text-left tw:font-normal',
                              !formState.billingStart &&
                                'tw:text-muted-foreground',
                            )}
                            disabled={isReadOnly}
                          >
                            <CalendarIcon />
                            {formState.billingStart
                              ? Intl.DateTimeFormat(
                                  new Intl.NumberFormat().resolvedOptions()
                                    .locale,
                                ).format(formState.billingStart)
                              : i18n.t('Containers:selectDate')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="tw:w-auto tw:p-0"
                          align="start"
                        >
                          <Calendar
                            mode="single"
                            selected={formState.billingStart ?? undefined}
                            onSelect={(date) =>
                              setFormState((prev) => ({
                                ...prev,
                                billingStart: date ?? null,
                              }))
                            }
                            disabled={{ before: minBillingDate }}
                            startMonth={minBillingDate}
                            defaultMonth={
                              formState.billingStart ?? minBillingDate
                            }
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </Field>
                </FieldSet>

                {!isReadOnly && formState.billingStartOption === 'custom' && (
                  <p className="tw:text-xs tw:text-muted-foreground tw:-mt-2">
                    {i18n.t('Assets:pastMonthsNotSelectableHint')}
                  </p>
                )}

                {/* Row 6: Last day of billing */}
                <FieldSet className="tw:grid tw:grid-cols-2">
                  <Field>
                    <FieldLabel>{i18n.t('Assets:lastDayOfBilling')}</FieldLabel>
                    <Select
                      value={formState.billingEndOption}
                      onValueChange={(value) =>
                        setFormState((prev) => ({
                          ...prev,
                          billingEndOption: value as 'none' | 'custom',
                          billingEnd: value === 'none' ? null : prev.billingEnd,
                        }))
                      }
                      disabled={isReadOnly}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={i18n.t('Common:select')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          {i18n.t('Assets:noEndDate')}
                        </SelectItem>
                        <SelectItem value="custom">
                          {i18n.t('Assets:custom')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field>
                    <FieldLabel className="tw:invisible">
                      {i18n.t('Common:dateTime')}
                    </FieldLabel>
                    {formState.billingEndOption === 'none' ? (
                      <Button
                        variant="outline"
                        className="tw:w-full tw:justify-start tw:text-left tw:font-normal"
                        disabled
                      >
                        <CalendarIcon />
                        {i18n.t('Assets:noEndDate')}
                      </Button>
                    ) : (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              'tw:w-full tw:justify-start tw:text-left tw:font-normal',
                              !formState.billingEnd &&
                                'tw:text-muted-foreground',
                            )}
                            disabled={isReadOnly}
                          >
                            <CalendarIcon />
                            {formState.billingEnd
                              ? Intl.DateTimeFormat(
                                  new Intl.NumberFormat().resolvedOptions()
                                    .locale,
                                ).format(formState.billingEnd)
                              : i18n.t('Containers:selectDate')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="tw:w-auto tw:p-0"
                          align="start"
                        >
                          <Calendar
                            mode="single"
                            selected={formState.billingEnd ?? undefined}
                            onSelect={(date) =>
                              setFormState((prev) => ({
                                ...prev,
                                billingEnd: date ?? null,
                              }))
                            }
                            disabled={{ before: minBillingEndDate }}
                            startMonth={minBillingEndDate}
                            defaultMonth={
                              formState.billingEnd ?? minBillingEndDate
                            }
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </Field>
                </FieldSet>

                {!isReadOnly && formState.billingEndOption === 'custom' && (
                  <p className="tw:text-xs tw:text-muted-foreground tw:-mt-2">
                    {i18n.t('Assets:lastDayOfBillingInclusiveHint')}
                  </p>
                )}

                {/* Custom Fields */}
                <AssetCustomFields
                  definitions={customFieldDefinitions}
                  values={formState.customFields}
                  onChange={(customFields) =>
                    setFormState((prev) => ({ ...prev, customFields }))
                  }
                  disabled={isReadOnly}
                />
              </FieldGroup>
            </>
          )}
        </DialogBody>
        {!loading && (
          <DialogFooter>
            {children}
            {isAddMode && (
              <>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSaving}
                >
                  {i18n.t('Common:cancel')}
                </Button>
                <Button onClick={handleSave} disabled={!canSave || isSaving}>
                  {isSaving && <Spinner />}
                  {i18n.t('Assets:addAsset')}
                </Button>
              </>
            )}
            {mode === 'edit' && (
              <>
                <Button variant="outline" onClick={handleClose}>
                  {i18n.t('Common:close')}
                </Button>
                <Button onClick={handleSave} disabled={!canSave || isSaving}>
                  {isSaving && <Spinner />}
                  {i18n.t('Common:save')}
                </Button>
              </>
            )}
            {mode === 'view' && (
              <Button variant="outline" onClick={handleClose}>
                {i18n.t('Common:close')}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
